import React, { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { SiteContent, defaultContent } from "./usePersistentContent";
import { reconstructObject, deepMerge } from "../lib/objectUtils";

export interface UseContentFromDBResult {
  content: SiteContent;
  setContent: React.Dispatch<React.SetStateAction<SiteContent>>;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  updateContent: (key: string, value: string) => Promise<boolean>;
  reset: () => void;
}

/**
 * THE MAIN HOOK - Fetch all site content from database
 * 
 * This is the ONLY hook you need to use for fetching content.
 * It fetches from Supabase database (flattened key-value structure)
 * and reconstructs it into the nested SiteContent format.
 * 
 * Falls back to minimal local defaults if the database is not configured.
 * Includes real-time updates when content changes in database.
 */
export function useContentFromDB(): UseContentFromDBResult {
  const [content, setContent] = useState<SiteContent>(defaultContent);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  type NestedObject = { [key: string]: unknown };

  const fetchContent = async (retryCount: number = 0): Promise<void> => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 1000; // 1 second

    try {
      setLoading(true);
      setError(null);

      // Check if Supabase is configured
      if (!process.env.REACT_APP_SUPABASE_URL || !process.env.REACT_APP_SUPABASE_ANON_KEY) {
        setContent(defaultContent);
        setLoading(false);
        return;
      }

      // Fetch all content entries
      const { data, error: fetchError } = await supabase
        .from('content')
        .select('key, value, type')
        .eq('language', 'he');

      if (fetchError) {
        // Retry logic for network errors
        if (retryCount < MAX_RETRIES && (fetchError.message.includes('network') || fetchError.message.includes('timeout'))) {
          console.warn(`⚠️ Retry attempt ${retryCount + 1}/${MAX_RETRIES} for content fetch`);
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retryCount + 1)));
          return fetchContent(retryCount + 1);
        }
        console.error('❌ Error fetching from Supabase:', fetchError);
        setError(fetchError.message);
        setContent(defaultContent);
        setLoading(false);
        return;
      }

      if (!data || data.length === 0) {
        console.warn('⚠️  No data found in Supabase, using local JSON data');
        setContent(defaultContent);
        setLoading(false);
        return;
      }
       // Filter out legacy JSON blobs when granular keys exist (e.g. reviews.items vs reviews.items.0.*)
       const filtered = data.filter((entry) => {
        if (entry.type !== "json") {
          return true;
        }

        const prefix = `${entry.key}.`;
        const hasGranularKeys = data.some((other) => other.key.startsWith(prefix));

        return !hasGranularKeys;
      });

      // Reconstruct the nested structure
      const reconstructed = reconstructObject(filtered);

      // Deep merge: DB values override defaults, but missing fields keep their defaults
      let mergedContent: SiteContent = deepMerge(defaultContent, reconstructed as Record<string, unknown>);

      // Normalize fields that may arrive as strings from the DB.
      // The deep merge preserves raw DB types (strings), so we coerce them
      // to the proper runtime types expected by consumers.
      const raw = mergedContent as unknown as Record<string, Record<string, unknown>>;
      if (raw.hero?.ctaEnabled !== undefined) {
        raw.hero.ctaEnabled = raw.hero.ctaEnabled === true || String(raw.hero.ctaEnabled) === 'true';
      }
      if (raw.promoModal?.enabled !== undefined) {
        raw.promoModal.enabled = raw.promoModal.enabled === true || String(raw.promoModal.enabled) === 'true';
      }
      if (raw.promoModal?.showOnFirstVisit !== undefined) {
        raw.promoModal.showOnFirstVisit = raw.promoModal.showOnFirstVisit === true || String(raw.promoModal.showOnFirstVisit) === 'true';
      }
      if (raw.promoModal?.showDelay !== undefined) {
        const delay = raw.promoModal.showDelay;
        raw.promoModal.showDelay = typeof delay === 'number' ? delay : parseInt(String(delay), 10) || 1000;
      }
      // Normalize rate limit fields
      const formLabels = (raw.contact as Record<string, unknown>)?.formLabels as Record<string, unknown> | undefined;
      if (formLabels?.rateLimitMaxSubmissions !== undefined) {
        const v = formLabels.rateLimitMaxSubmissions;
        formLabels.rateLimitMaxSubmissions = typeof v === 'number' ? v : parseInt(String(v), 10) || 3;
      }
      if (formLabels?.rateLimitTimeWindow !== undefined) {
        const v = formLabels.rateLimitTimeWindow;
        formLabels.rateLimitTimeWindow = typeof v === 'number' ? v : parseInt(String(v), 10) || 15;
      }

      // Apply named team member overrides if provided as meaningful keys
      // Accepts keys like: team.member.<memberKey>.name, photo, role, bio, label
      try {
        const teamObj = reconstructed?.team as NestedObject | undefined;
        const namedMembers = ((teamObj?.member ?? {}) as Record<string, Record<string, string>>);
        if (namedMembers && typeof namedMembers === 'object') {
          const result: SiteContent = JSON.parse(JSON.stringify(mergedContent)) as SiteContent;
          const defaultOrderKeys = (defaultContent.team.members || []).map((m) => m.key);

          // Build index by key for fast lookup
          const keyToIndex: Record<string, number> = {};
          result.team.members.forEach((m, idx) => {
            keyToIndex[m.key] = idx;
          });

          for (const [memberKey, fields] of Object.entries(namedMembers)) {
            let idx = keyToIndex[memberKey];
            if (typeof idx !== 'number') {
              // Insert new member at the end, but if it exists in defaults, keep default ordering
              const defaultIdx = defaultOrderKeys.indexOf(memberKey);
              if (defaultIdx >= 0) {
                // Ensure array has a member at that index
                if (!result.team.members[defaultIdx] || result.team.members[defaultIdx].key !== memberKey) {
                  result.team.members.splice(defaultIdx, 0, {
                    key: memberKey,
                    name: '',
                    role: '',
                    photo: '',
                    bio: '',
                    label: ''
                  });
                }
                idx = defaultIdx;
              } else {
                result.team.members.push({ key: memberKey, name: '', role: '', photo: '', bio: '', label: '' });
                idx = result.team.members.length - 1;
              }
              keyToIndex[memberKey] = idx;
            }

            const target = result.team.members[idx];
            if (typeof fields.name === 'string') target.name = fields.name;
            if (typeof fields.role === 'string') target.role = fields.role;
            if (typeof fields.photo === 'string') target.photo = fields.photo;
            if (typeof fields.photoPosition === 'string') target.photoPosition = fields.photoPosition;
            if (typeof fields.bio === 'string') target.bio = fields.bio;
            if (typeof fields.label === 'string') target.label = fields.label;
          }

          mergedContent = result;
        }
      } catch {
        // ignore mapping errors and keep mergedContent as-is
      }

      setContent(mergedContent);
      setLoading(false);

    } catch (err) {
      console.error('💥 Exception while fetching content:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setContent(defaultContent);
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchContent();

    // Subscribe to real-time updates.
    // Wrapped in try-catch because the Supabase Realtime WebSocket can throw
    // if blocked by Content Security Policy or network restrictions (e.g. "The operation is insecure").
    // Real-time updates are a nice-to-have; the app must not crash if they fail.
    let subscription: ReturnType<typeof supabase.channel> | null = null;
    try {
      subscription = supabase
        .channel('content_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'content'
          },
          () => {
            void fetchContent();
          }
        )
        .subscribe();
    } catch (err) {
      console.warn('⚠️ Realtime subscription failed (CSP or network issue):', err);
    }

    return () => {
      if (subscription) {
        try {
          void subscription.unsubscribe();
        } catch {
          // Silently ignore unsubscribe errors
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = (): void => setContent(defaultContent);

  return {
    content,
    setContent,
    loading,
    error,
    refetch: fetchContent,
    reset,
    updateContent: async (key: string, value: string) => {
      try {
        // Determine section and type from key
        let section = key.split('.')[0];
        // Special handling for carouselTitle and carouselDescription
        if (key === 'carouselTitle' || key === 'carouselDescription') {
          section = 'carousel';
        }
        // Determine type - check if it's a boolean/number or URL
        let type = 'text';
        if (value.startsWith('http')) {
          type = 'url';
        } else if (value === 'true' || value === 'false') {
          type = 'text'; // Store as text, will be parsed as boolean
        } else if (!isNaN(Number(value)) && value.trim() !== '') {
          type = 'text'; // Store numbers as text
        }
        
        // Use upsert to insert if doesn't exist, update if it does
        const { error: upsertError } = await supabase
          .from('content')
          .upsert({
            key,
            value,
            type,
            section,
            language: 'he'
          }, {
            onConflict: 'key'
          });
        
        if (upsertError) {
          console.error(`❌ Error updating ${key}:`, upsertError);
          return false;
        }
        
        await fetchContent(); // Refetch to update UI
        return true;
      } catch (err) {
        console.error(`💥 Exception updating ${key}:`, err);
        return false;
      }
    }
  };
}

